import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../common/api';
import { trackEvent, TrackingEventTypes } from '../common/tracking';
import { RootState } from './store';

export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_PAGE = 0;

export interface PatientSummary {
  id: string;
  patientId: number;
  code: string;
  externalId: string;
  accountValidated: boolean;
  accountValidatedDate: string;
  status: string;
  lastQuestionnaireDate: string | null; // need to store date as string (serialization issue in redux)
  accountDesactivated: boolean;
  doctorId: string;
  hospitalId: string;
  studyEnded: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  watchType?: string;
  inclusionInfo: PatientInclusionInfo | null;
  visits: Visit[] | null;
  inclusionCompleted: boolean;
}

export interface Comorbidities {
  comorbiditySleepApnea: boolean;
  comorbidityCardiovascular: boolean;
  comorbidityAnxiety: boolean;
  comorbidityNeurological: boolean;
  comorbidityEndocrine: boolean;
  comorbidityOther: boolean;
  [key: string]: boolean; // Signature d'index pour indiquer que les clés sont des strings
}

export interface Medication {
  id: string | undefined;
  name: string;
  quantity: number;
  unit: string;
  frequency: string;
  startDate: string;
}

export interface Hospitalization {
  id: string | undefined;
  hospital: string;
  reason: string;
  startDate: string; // formatted string for date
  endDate: string;
}

export interface Visit {
  date: string; // formatted string for date
  medications: Medication[];
  suicideAttempt: boolean;
  newDiagnostic: string;
}

export interface PatientInclusionInfo {
  validated: boolean | undefined;
  yearOfBirth: number;
  sex: string;
  height: number;
  weight: number;
  maritalStatus: string;
  educationLevel: string;
  professionalStatus: string;
  deptCode: string;
  alcohol: boolean;
  alcoholUnits: string;
  substances: boolean;
  substancesType: string[];
  substancesFrequency: string;
  pregnancy: boolean;
  breastfeeding: boolean;
  tobacco: boolean;
  tobaccoFrequency: string;
  comorbidities: Comorbidities;
  medications: Medication[];
  familyPsychiatricDisorder: boolean;
  familyPsychiatricDisorders: string[];
  familyPsychiatricDisorderRelationshipDegree: number | null;
  episodes: number;
  relapsed: string;
  hospitalizations: Hospitalization[];
  suicideAttempts: number;
}

export const defaultVisit: Visit = {
  date: new Date().toISOString(),
  medications: [],
  suicideAttempt: false,
  newDiagnostic: '',
};

export const defaultPatientSummary: PatientSummary = {
  id: 'UNKNOWN',
  patientId: -1,
  code: '',
  externalId: '',
  accountValidated: false,
  accountValidatedDate: '',
  lastQuestionnaireDate: '',
  accountDesactivated: false,
  status: '',
  doctorId: '',
  hospitalId: '',
  studyEnded: false,
  createdAt: '',
  updatedAt: '',
  watchType: 'NONE',
  inclusionInfo: null,
  visits: null,
  inclusionCompleted: false,
};

export const defaultInclusionInfo: PatientInclusionInfo = {
  yearOfBirth: 0,
  sex: '',
  height: 0,
  weight: 0,
  maritalStatus: '',
  educationLevel: '',
  professionalStatus: '',
  deptCode: '',
  alcohol: false,
  alcoholUnits: '',
  substances: false,
  substancesType: [],
  substancesFrequency: '',
  pregnancy: false,
  breastfeeding: false,
  tobacco: false,
  tobaccoFrequency: '',
  comorbidities: {
    comorbiditySleepApnea: false,
    comorbidityCardiovascular: false,
    comorbidityAnxiety: false,
    comorbidityNeurological: false,
    comorbidityEndocrine: false,
    comorbidityOther: false,
  },
  medications: [],
  familyPsychiatricDisorder: false,
  familyPsychiatricDisorders: [],
  familyPsychiatricDisorderRelationshipDegree: 0,
  episodes: 0,
  hospitalizations: [],
  suicideAttempts: 0,
  relapsed: '',
  validated: false,
};

export interface Pagination {
  page: number;
  pageSize: number;
}

export enum SortOrder {
  Asc = 'asc',
  Desc = 'desc',
}
export interface SortModel {
  sortKey: string;
  sortOrder: SortOrder;
}

export interface PatientState {
  patients: PaginatedPatientSummary | null;
  patientEdited: PatientSummary | null;
  loading: boolean;
  error: string | null;
  pagination: Pagination;
  sortModel: SortModel; // Add sortConfig to the state
}

const initialState: PatientState = {
  patients: null,
  patientEdited: null,
  loading: false,
  error: null,
  pagination: { page: DEFAULT_PAGE, pageSize: DEFAULT_PAGE_SIZE },
  sortModel: { sortKey: 'patientId', sortOrder: SortOrder.Asc }, // Initialize sortConfig
};

export interface SortConfig {
  sortKey?: string;
  sortOrder?: string;
  page?: number;
  pageSize?: number;
}

export interface PaginatedPatientSummary {
  patients: PatientSummary[];
  page: number;
  pageSize: number;
  total: number;
}

const messageFromError = (error: any) => {
  if (error.response) {
    // Server responded with a status other than 200 range
    return error.response.data.message;
  } else if (error.request) {
    // Request was made but no response received
    return 'No response from server';
  } else {
    // Something else happened while setting up the request
    return error.message;
  }
};

/**
 * Fetch de patient list from the backend
 */
export const fetchPatients = createAsyncThunk<PaginatedPatientSummary, void, { rejectValue: string; state: RootState }>(
  'patients/fetchPatients',
  async (_, thunkAPI) => {
    const pagination: Pagination = thunkAPI.getState().patients.pagination;
    const sortModel: SortModel = thunkAPI.getState().patients.sortModel;
    try {
      const response = await api.get('api/v1/patients', {
        params: {
          sort: sortModel.sortKey,
          order: sortModel.sortOrder,
          page: Math.max(pagination.page, 0),
          pageSize: pagination.pageSize,
        },
      });
      return response.data as PaginatedPatientSummary;
    } catch (error) {
      return thunkAPI.rejectWithValue(`Failed to load patients - ${messageFromError(error)}`);
    }
  },
);

/**
 * Fetch patient from the backend
 */
export const fetchPatient = createAsyncThunk<PatientSummary, { id: string }, { rejectValue: string }>('patients/fetchPatient', async (data, thunkAPI) => {
  try {
    const response = await api.get(`api/v1/patients/${data.id}`);
    return response.data as PatientSummary;
  } catch (error) {
    return thunkAPI.rejectWithValue(`Failed to load patient - ${messageFromError(error)}`);
  }
});

/**
 * Create a new patient in the backend and update the patient list
 */
export const createPatient = createAsyncThunk<PatientSummary, Partial<InitInfo>, { rejectValue: string; state: RootState }>(
  'patients/createPatient',
  async (patientData, thunkAPI) => {
    try {
      const data = {
        code: patientData.code,
        doctorId: patientData.doctorId,
        externalId: patientData.externalId,
        phone: patientData.phone,
      };
      const response = await api.post('/api/v1/patients', data);
      await thunkAPI.dispatch(fetchPatients());
      return response.data;
    } catch (error) {
      console.log(error);
      return thunkAPI.rejectWithValue(`Failed to create patient - ${messageFromError(error)}`);
    }
  },
);

/**
 * Complete a patient in the backend by adding the inclusion info
 */
export const completePatientWithInclusionInfo = createAsyncThunk<
  PatientSummary,
  { id: string; inclusionInfo: Partial<PatientInclusionInfo> },
  { rejectValue: string; state: RootState }
>('patients/completePatientWithInclusionInfo', async (patientData, thunkAPI) => {
  try {
    const response = await api.post('/api/v1/patients/' + patientData.id + '/inclusion-info', patientData.inclusionInfo);
    await thunkAPI.dispatch(fetchPatients());
    return response.data as PatientSummary;
  } catch (error) {
    console.log(error);
    return thunkAPI.rejectWithValue(`Failed to complete patient - ${messageFromError(error)}`);
  }
});

/**
 * Complete a patient in the backend by adding the inclusion info
 */
export const addVisitToPatient = createAsyncThunk<PatientSummary, { id: string; visit: Partial<Visit> }, { rejectValue: string }>(
  'patients/addVisitToPatient',
  async (patientData, thunkAPI) => {
    try {
      const response = await api.post('/api/v1/patients/' + patientData.id + '/add-visit', patientData.visit);
      return response.data as PatientSummary;
    } catch (error) {
      console.log(error);
      return thunkAPI.rejectWithValue(` Failed to add visit to patient - ${messageFromError(error)}`);
    }
  },
);

export const disconnectWatch = createAsyncThunk<PatientSummary, { id: string }, { rejectValue: string }>('patients/disconnectWatch', async (data, thunkAPI) => {
  try {
    const response = await api.post(`/api/v1/patients/${data.id}/disconnect-watch`);
    await thunkAPI.dispatch(fetchPatients() as any);
    return response.data as PatientSummary;
  } catch (error) {
    return thunkAPI.rejectWithValue(`Failed to disconnect watch - ${messageFromError(error)}`);
  }
});

const patientSlice = createSlice({
  name: 'patient',
  initialState,
  reducers: {
    setPagination(state, action: PayloadAction<Pagination>) {
      state.pagination = action.payload;
    },
    setSortModel(state, action: PayloadAction<SortModel>) {
      state.sortModel = action.payload;
    },
    resetPatientEdited(state) {
      state.patientEdited = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPatients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatients.fulfilled, (state, action: PayloadAction<PaginatedPatientSummary>) => {
        state.patients = action.payload;
        state.loading = false;
      })
      .addCase(fetchPatients.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch all patients';
      })
      .addCase(fetchPatient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatient.fulfilled, (state, action: PayloadAction<PatientSummary>) => {
        state.patientEdited = action.payload;
        state.loading = false;
      })
      .addCase(fetchPatient.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch a patient';
      })
      .addCase(createPatient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPatient.fulfilled, (state, action: PayloadAction<PatientSummary>) => {
        state.patientEdited = action.payload;
        state.loading = false;
        trackEvent(TrackingEventTypes.ADD_PATIENT_COMPLETE, { patient_id: state.patientEdited.patientId });
      })
      .addCase(createPatient.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create patient';
      })
      .addCase(completePatientWithInclusionInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completePatientWithInclusionInfo.fulfilled, (state, action: PayloadAction<PatientSummary>) => {
        state.patientEdited = action.payload;
        state.loading = false;
      })
      .addCase(completePatientWithInclusionInfo.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create patient';
      })
      .addCase(addVisitToPatient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addVisitToPatient.fulfilled, (state, action: PayloadAction<PatientSummary>) => {
        state.patientEdited = action.payload;
        state.loading = false;
      })
      .addCase(addVisitToPatient.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create patient';
      })
      .addCase(disconnectWatch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(disconnectWatch.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(disconnectWatch.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'Failed to disconnect watch';
      });
  },
});
export default patientSlice.reducer;
export const { setPagination, setSortModel, resetPatientEdited } = patientSlice.actions;

export interface InitInfo {
  inclusionCriteria: string[];
  exclusionCriteria: string[];
  code: string;
  externalId: string;
  phone: string;
  doctorId: string;
}
